/**
 * artificialRelief.ts - the artificial relief, shaded over ground no altimeter
 * ever flew to, on the globe above the ORBIT edge and on the streamed tiles
 * below it.
 *
 * `tools/dtm/fill_height.py` writes one equirect map per body,
 * `public/art/atlas/planets/<id>_artificial_height_2k.webp`, whose R and G are
 * the two horizontal slope components about 128 and whose alpha is 255 where
 * the field HAS data and 0 where an instrument measured the relief. The chunk
 * here perturbs the globe's shading normal by those slopes and touches nothing
 * else: no colour is replaced and no geometry moves, so with `fxReliefOn` at 0
 * the frame is the one the build before this role drew.
 *
 * It lands after `normal_fragment_maps`, which is where three's own bump and
 * normal maps would have written, so the lighting that follows reads the
 * perturbed normal and the rest of the material is untouched.
 */

import * as THREE from 'three';

/** The uniforms the chunk reads, owned by whoever drew the body. */
export interface ArtificialReliefRef {
  readonly map: { value: THREE.Texture | null };
  /** 1 only while the layer is on AND this body's map has landed. */
  readonly on: { value: number };
  /** The slope a stored 0 or 255 stands for: `SLOPE_FULL_SCALE` in the bake. */
  readonly scale: { value: number };
  /**
   * `descent-dojo --artificial --inject relief-over-measured`: the control.
   *
   * At 1 the perturbation ignores the map's alpha and shades measured ground
   * too, which is the one thing this role must never do.
   */
  readonly force: { value: number };
}

export interface ArtificialReliefHandle {
  readonly ref: ArtificialReliefRef;
  /** The Layers row. */
  setOn(on: boolean): void;
  /** The map itself, or null while it is in flight or failed. */
  setMap(map: THREE.Texture | null): void;
  /** The control: shade over measured ground too. */
  setOverMeasured(on: boolean): void;
  /**
   * The other control: fetch the map, switch the row, perturb nothing.
   *
   * The frame it leaves is the one the build before this role drew, since `on`
   * at 0 skips the chunk whole - which is the build the relief bar has to be
   * able to read as red.
   */
  setInert(on: boolean): void;
  /** Whether the frame is drawing it, for a reader that has to report what it draws. */
  drawn(): boolean;
}

/**
 * `load` is called once, the first time the layer asks for this relief: the map
 * is the layer's own asset and nothing else reads it, so a profile with the row
 * switched off makes no request for it at all.
 */
export function createArtificialRelief(fullScale: number,
  load?: () => void): ArtificialReliefHandle {
  const ref: ArtificialReliefRef = {
    map: { value: null }, on: { value: 0 }, scale: { value: fullScale }, force: { value: 0 },
  };
  let layerOn = false;
  let asked = false;
  let inert = false;
  function sync(): void {
    ref.on.value = layerOn && !inert && ref.map.value !== null ? 1 : 0;
  }
  return {
    ref,
    setOn(on: boolean): void {
      layerOn = on;
      if (on && !asked && load) { asked = true; load(); }
      sync();
    },
    setMap(map: THREE.Texture | null): void { ref.map.value = map; sync(); },
    setOverMeasured(on: boolean): void { ref.force.value = on ? 1 : 0; },
    setInert(on: boolean): void { inert = on; sync(); },
    drawn(): boolean { return ref.on.value > 0; },
  };
}

const RELIEF_DECL = 'uniform sampler2D fxReliefMap;\nuniform float fxReliefOn;\n'
  + 'uniform float fxReliefScale;\nuniform float fxReliefAll;\n'
  + 'varying vec3 vReliefEast;\nvarying vec3 vReliefNorth;\n';

/**
 * The perturbation itself.
 *
 * Guarded on the switch rather than weighted by it, so the layer off leaves
 * `normal` the exact bits the lighting had before this chunk existed - a
 * renormalise of an already-normalised vector is not always the identity.
 */
const RELIEF_TILT = '  if (fxReliefOn > 0.0) {\n'
  + '    vec4 fxRel = texture2D(fxReliefMap, vMapUv);\n'
  + '    float fxRelW = max(fxRel.a, fxReliefAll);\n'
  + '    vec2 fxRelS = (fxRel.rg - 0.50196078) * 2.0078740 * fxReliefScale * fxRelW;\n'
  + '    normal = normalize(normal - fxRelS.x * normalize(vReliefEast)\n'
  + '      - fxRelS.y * normalize(vReliefNorth));\n'
  + '  }\n';

/**
 * The east and north tangents the slopes are expressed in, carried from the
 * vertex shader.
 *
 * Built off the object-space position rather than off screen derivatives: the
 * map's own columns run east and its rows north, and a tangent recovered from
 * `dFdx` would be a screen direction that happens to agree only where the
 * sphere faces the camera. `normalMatrix` puts them in the space `normal` is
 * already in.
 */
const RELIEF_TANGENTS = '  vec3 fxRelUp = normalize(position);\n'
  + '  vec3 fxRelE = vec3(fxRelUp.z, 0.0, -fxRelUp.x);\n'
  + '  fxRelE = length(fxRelE) > 1.0e-5 ? normalize(fxRelE) : vec3(1.0, 0.0, 0.0);\n'
  + '  vReliefEast = normalMatrix * fxRelE;\n'
  + '  vReliefNorth = normalMatrix * cross(fxRelUp, fxRelE);\n';

/** Inject the chunk into a `MeshStandardMaterial`'s compiled shader. */
export function applyArtificialRelief(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: ArtificialReliefRef): void {
  shader.uniforms['fxReliefMap'] = ref.map;
  shader.uniforms['fxReliefOn'] = ref.on;
  shader.uniforms['fxReliefScale'] = ref.scale;
  shader.uniforms['fxReliefAll'] = ref.force;
  shader.vertexShader = `varying vec3 vReliefEast;\nvarying vec3 vReliefNorth;\n${
    shader.vertexShader}`
    .replace('#include <begin_vertex>', `#include <begin_vertex>\n${RELIEF_TANGENTS}`);
  shader.fragmentShader = RELIEF_DECL
    + `${shader.fragmentShader}`
      .replace('#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>\n${RELIEF_TILT}`);
}

/**
 * The direction-sampled read of the same map, antimeridian fix included.
 *
 * The globe reads the map through `vMapUv`, which on its sphere is
 * `0.5 + atan(-dir.z, dir.x) / 2pi` by `0.5 + asin(dir.y) / pi` - the identity
 * the sibling read in `artificialFill.ts` is built on. A tile has no uv of its
 * own for this map, because no pyramid carries the relief: it is one equirect
 * beside the globe's picture. So the tile recovers the same uv from the
 * direction and samples with explicit gradients, which is what keeps a wrap
 * column off the antimeridian.
 */
const TILE_RELIEF_READ = '    vec3 fxRelD = normalize(vReliefDir);\n'
  + '    vec2 fxRelUv = vec2(\n'
  + '      0.5 + atan(-fxRelD.z, fxRelD.x) * 0.1591549430918953,\n'
  + '      0.5 + asin(clamp(fxRelD.y, -1.0, 1.0)) * 0.3183098861837907);\n'
  + '    vec2 fxRelDx = dFdx(fxRelUv);\n'
  + '    vec2 fxRelDy = dFdy(fxRelUv);\n'
  + '    if (abs(fxRelDx.x) > 0.5) fxRelDx.x -= sign(fxRelDx.x);\n'
  + '    if (abs(fxRelDy.x) > 0.5) fxRelDy.x -= sign(fxRelDy.x);\n'
  + '    vec4 fxRel = textureGrad(fxReliefMap, fxRelUv, fxRelDx, fxRelDy);\n';

/** The same tilt, over a texel the tile sampled by direction. */
const TILE_RELIEF_TILT = '  if (fxReliefOn > 0.0) {\n'
  + TILE_RELIEF_READ
  + '    float fxRelW = max(fxRel.a, fxReliefAll);\n'
  + '    vec2 fxRelS = (fxRel.rg - 0.50196078) * 2.0078740 * fxReliefScale * fxRelW;\n'
  + '    normal = normalize(normal - fxRelS.x * normalize(vReliefEast)\n'
  + '      - fxRelS.y * normalize(vReliefNorth));\n'
  + '  }\n';

/**
 * The same tangents, off a tile's own vertex.
 *
 * `position + fxReliefOrigin` is the vertex's atlas-frame offset from the
 * body's centre - `origin` is the mesh's own centre, the one `bodyToAtlas`
 * builds for `mesh.position` - and the atlas frame's axes are the globe's
 * object-space axes, so it normalizes to the direction the globe takes its own
 * uv along. That is the reconstruction `applyTileArtificialFallback` makes for
 * the fill, and the two have to agree: this shades the ground that paints.
 */
const TILE_RELIEF_TANGENTS = '  vec3 fxRelUp = normalize(position + fxReliefOrigin);\n'
  + '  vReliefDir = fxRelUp;\n'
  + '  vec3 fxRelE = vec3(fxRelUp.z, 0.0, -fxRelUp.x);\n'
  + '  fxRelE = length(fxRelE) > 1.0e-5 ? normalize(fxRelE) : vec3(1.0, 0.0, 0.0);\n'
  + '  vReliefEast = normalMatrix * fxRelE;\n'
  + '  vReliefNorth = normalMatrix * cross(fxRelUp, fxRelE);\n';

/**
 * The globe's relief, shading a streamed tile of the same body.
 *
 * Normals and not vertices: a tile mesh's heights are the measured field the
 * surface clamp, the skirt depth and the ground under a rover are all read
 * off, and invented relief has no business moving any of them. Shading is also
 * what keeps the hand-over at the ORBIT edge quiet - the globe perturbs the
 * same normal by the same slopes out of the same map, so a pyramid opening
 * under the eye changes the resolution of the picture and not the shape of the
 * light on it.
 */
export function applyTileArtificialRelief(shader: THREE.WebGLProgramParametersWithUniforms,
  ref: ArtificialReliefRef, origin: THREE.Vector3): void {
  shader.uniforms['fxReliefMap'] = ref.map;
  shader.uniforms['fxReliefOn'] = ref.on;
  shader.uniforms['fxReliefScale'] = ref.scale;
  shader.uniforms['fxReliefAll'] = ref.force;
  shader.uniforms['fxReliefOrigin'] = { value: origin };
  shader.vertexShader = 'uniform vec3 fxReliefOrigin;\nvarying vec3 vReliefDir;\n'
    + `varying vec3 vReliefEast;\nvarying vec3 vReliefNorth;\n${shader.vertexShader}`
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${TILE_RELIEF_TANGENTS}`);
  shader.fragmentShader = `${RELIEF_DECL}varying vec3 vReliefDir;\n${shader.fragmentShader}`
    .replace('#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>\n${TILE_RELIEF_TILT}`);
}

/** The program cache key's own suffix: three's does not vary with a hook's content. */
export function reliefKey(ref: ArtificialReliefRef | null): string {
  return ref === null ? '' : '-rel1';
}

/** Userdata key: which relief this material shades by, for the one writer of its hook. */
const RELIEF_REF = 'fxcivReliefRef';

/** Say which artificial relief this globe carries, the way `markArtificialFill` does. */
export function markArtificialRelief(mat: THREE.Material, ref: ArtificialReliefRef): void {
  mat.userData[RELIEF_REF] = ref;
}

export function reliefRefOf(mat: THREE.Material): ArtificialReliefRef | null {
  const ref = mat.userData[RELIEF_REF] as ArtificialReliefRef | undefined;
  return ref ?? null;
}
